import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FieldArray, Formik } from 'formik';
import { get, isArray, isEmpty, uniqueId } from 'lodash';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import Features from '@dashboard/enums/features';
import usePage from '@dashboard/hooks/usePage';
import { confirm } from '@dashboard/lib/confirm';

import { countries } from '@dashboard/services/countries/countries';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import {
  elementFieldTypeEnums,
  getDataFieldTypesForElement,
  getElement,
  getElementFieldTypes,
} from '@dashboard/services/pageFieldTypes';

import Button from '@dashboard/components/Button';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import Collapsible from '@dashboard/components/Collapsible';
import CopyTextWithTooltip from '@dashboard/components/CopyTextWithTooltip';
import { FormElement, Label } from '@dashboard/components/Form';
import { Checkbox, FormSubmit, Listbox, Select, TextInput } from '@dashboard/components/Formik';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Link from '@dashboard/components/Link';
import Reorderable from '@dashboard/components/Reorderable';
import { InfoTooltip } from '@dashboard/components/Tooltip';

import { DragIcon } from '@dashboard/icons/DragIcon';

import { useFieldEdit } from '../FieldEditContext';
import { useFieldHelpers } from '../useFieldHelpers';
import ShowHideLogicField from './ShowHideLogicField';

const FormElementInput = styled.div`
  width: 100%;
`;

const propTypes = {
  onSubmit: PropTypes.func.isRequired,
  field: PropTypes.object.isRequired,
  actions: PropTypes.element.isRequired,
  disabled: PropTypes.bool,
};

const FieldEdit = ({ field, fieldKey, disabled }) => {
  const { page } = usePage();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { onCancelAddField, onSaveField, onDirty } = useFieldHelpers(field._id || field.key);
  const { setIsEditingField } = useFieldEdit();

  const elementFieldTypes = getElementFieldTypes(page.type);

  return (
    <div>
      <Formik
        initialValues={{
          label: field.label,
          element: field.element,
          options: field.options,
          placeholder: field.placeholder,
          type: field.type,
          required: field.required,
          reference: field.reference,
          hidden: field.hidden,
          limitAllowedCountries: field.limitAllowedCountries,
          showSelectedDialCode: field.showSelectedDialCode ?? true,
          defaultValue: field.defaultValue,
          showHideLogic: field.showHideLogic,
          minValue: field.minValue,
          maxValue: field.maxValue,
        }}
        onSubmit={async (values, actions) => {
          actions.setStatus(undefined);

          // unset type for elements that don't support types
          if (values.element === 'quantity' || values.element === 'checkbox') {
            values.type = undefined;
          }

          // Convert string boolean values to actual booleans for checkbox elements
          if (values.element === 'checkbox' && values.defaultValue?.enabled) {
            if (values.defaultValue.value === 'true') {
              values.defaultValue.value = true;
            } else if (values.defaultValue.value === 'false') {
              values.defaultValue.value = false;
            }
          }

          const { error, data } = await onSaveField(values);

          if (error) {
            const { status, errors } = transformApiErrorToFormik(error);

            if (!isEmpty(errors)) {
              actions.setErrors(errors);
            } else {
              actions.setStatus(status);
            }
          } else {
            // Convert string boolean values to actual booleans for checkbox elements
            if (data.element === 'checkbox' && data.defaultValue?.enabled) {
              if (data.defaultValue.value === 'true') {
                data.defaultValue.value = true;
              } else if (data.defaultValue.value === 'false') {
                data.defaultValue.value = false;
              }
            }

            actions.resetForm({
              values: {
                label: data.label,
                element: data.element,
                options: data.options,
                placeholder: data.placeholder,
                type: data.type,
                required: data.required,
                reference: data.reference,
                hidden: data.hidden,
                limitAllowedCountries: data.limitAllowedCountries,
                showSelectedDialCode: data.showSelectedDialCode,
                defaultValue: data.defaultValue,
                showHideLogic: data.showHideLogic,
                minValue: data.minValue,
                maxValue: data.maxValue,
              },
            });
          }
        }}
      >
        {({ handleSubmit, dirty, status, values, setFieldValue, errors }) => {
          const dataFieldTypes = getDataFieldTypesForElement(values.element);

          useEffect(() => {
            const element = getElement(values.element);

            if (!values.label && element.defaultLabel) {
              setFieldValue('label', element.defaultLabel);
            }

            if (!values.placeholder && element.defaultPlaceholder) {
              if (values.element === 'phone') {
                return;
              }

              setFieldValue('placeholder', element.defaultPlaceholder);
            }

            {
              /* // reset default value for country field
            if (values.element === 'country' && values.defaultValue?.value) {
              setFieldValue('defaultValue.value', '');
              setFieldValue('defaultValue.enabled', false);
            } */
            }
          }, [values.element]);

          onDirty(dirty);

          return (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <FormElement>
                  <Label for={`${fieldKey}_element`}>Field type *</Label>
                  <FormElementInput>
                    <Select id={`${fieldKey}_element`} name="element" autoFocus>
                      {Object.values(elementFieldTypes).map(elementFieldType => {
                        const element = getElement(elementFieldType);

                        return (
                          <option value={element.name} key={element.name}>
                            {element.label}
                          </option>
                        );
                      })}
                    </Select>
                  </FormElementInput>
                  <FormError name="element" />
                </FormElement>

                <FormElement>
                  <Label for={`${fieldKey}_label`}>Label *</Label>
                  <FormElementInput>
                    <TextInput
                      id={`${fieldKey}_label`}
                      name="label"
                      type="text"
                      placeholder="Label"
                    />
                  </FormElementInput>
                  <FormError name="label" />
                </FormElement>

                {values.element === elementFieldTypeEnums.SELECT && (
                  <SelectField
                    options={values.options}
                    onChange={options => setFieldValue('options', options)}
                    field={field}
                    fieldKey={fieldKey}
                    errors={errors}
                  />
                )}

                {values.element === elementFieldTypeEnums.MULTIPLE_CHOICE && (
                  <OptionsField
                    options={values.options}
                    onChange={options => setFieldValue('options', options)}
                    field={field}
                    fieldKey={fieldKey}
                    errors={errors}
                  />
                )}

                {values.element !== 'checkbox' &&
                  values.element !== 'quantity' &&
                  values.element !== elementFieldTypeEnums.MULTIPLE_CHOICE && (
                    <FormElement>
                      <Label for={`${fieldKey}_placeholder`}>Placeholder</Label>
                      <FormElementInput>
                        <TextInput
                          id={`${fieldKey}_placeholder`}
                          name="placeholder"
                          type="text"
                          placeholder="Placeholder"
                        />
                      </FormElementInput>
                      <FormError name="placeholder" />
                    </FormElement>
                  )}

                {dataFieldTypes && dataFieldTypes.length > 0 && (
                  <FormElement>
                    <Label for={`${fieldKey}_type`}>
                      Data type
                      <InfoTooltip>
                        Used to store field data in a predictable manner. Please note that you must
                        use Customer Name and Shipping Address if you wish to use any of the
                        shipping address data types.
                      </InfoTooltip>
                    </Label>

                    <FormElementInput>
                      <Select name="type" id={`${fieldKey}_type`}>
                        <option value="">Select data type</option>
                        {getDataFieldTypesForElement(values.element).map((type, index) => (
                          <option key={index} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Select>
                    </FormElementInput>

                    <FormError name="type" />
                  </FormElement>
                )}

                {values.element !== 'quantity' && (
                  <FormElement>
                    <Checkbox.Wrapper>
                      <Checkbox id={`${fieldKey}_required`} name="required" />
                      <Checkbox.Label htmlFor={`${fieldKey}_required`}>Required</Checkbox.Label>
                    </Checkbox.Wrapper>

                    <FormError name="required" />
                  </FormElement>
                )}

                <Collapsible.Root open={settingsOpen} onOpenChange={setSettingsOpen}>
                  <Collapsible.Trigger className="flex items-center gap-x-1 mt-4 text-gray-700 hover:text-gray-900 font-medium text-sm">
                    <>
                      <span>Additional settings</span>
                      {settingsOpen ? (
                        <ChevronUpIcon className="w-4 h-4" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </>
                  </Collapsible.Trigger>

                  <Collapsible.Content className="CollapsibleContentAnimation mt-4">
                    <FormElement className="space-y-1.5">
                      {(values.element === 'country' || values.element === 'phone') && (
                        <>
                          <Checkbox.Wrapper>
                            <Checkbox
                              id={`${fieldKey}_limitAllowedCountries.enabled`}
                              name="limitAllowedCountries.enabled"
                            />
                            <Checkbox.Label htmlFor={`${fieldKey}_limitAllowedCountries.enabled`}>
                              Limit allowed countries
                            </Checkbox.Label>
                          </Checkbox.Wrapper>

                          {values.limitAllowedCountries?.enabled && (
                            <FormElement className="ml-[26px] !mb-4">
                              <FormElementInput>
                                <Listbox
                                  id={`${fieldKey}_limitAllowedCountries.countries`}
                                  name="limitAllowedCountries.countries"
                                  options={countries.map(country => ({
                                    name: country.name,
                                    value: country.alpha2,
                                  }))}
                                  value={values.limitAllowedCountries.countries}
                                  onChange={countries => {
                                    // if only one country, enable showSelectedDialCode
                                    if (
                                      countries.length === 1 &&
                                      values.element === 'phone' &&
                                      values.showSelectedDialCode === undefined
                                    ) {
                                      setFieldValue('showSelectedDialCode', true);
                                    }
                                  }}
                                  placeholder="Select allowed countries"
                                  multiple
                                  selectAll
                                  selectAllLabel="All countries"
                                />
                              </FormElementInput>
                            </FormElement>
                          )}

                          <FormError name="allowedCountries" />
                        </>
                      )}

                      {values.element === 'phone' &&
                        values.limitAllowedCountries?.enabled &&
                        values.limitAllowedCountries.countries?.length === 1 && (
                          <Checkbox.Wrapper>
                            <Checkbox
                              id={`${fieldKey}_showSelectedDialCode`}
                              name="showSelectedDialCode"
                            />
                            <Checkbox.Label htmlFor={`${fieldKey}_showSelectedDialCode`}>
                              Show dial code
                            </Checkbox.Label>
                          </Checkbox.Wrapper>
                        )}

                      <Checkbox.Wrapper>
                        <Checkbox
                          id={`${fieldKey}_defaultValue.enabled`}
                          name="defaultValue.enabled"
                        />
                        <Checkbox.Label htmlFor={`${fieldKey}_defaultValue.enabled`}>
                          Default value
                        </Checkbox.Label>
                        <FormError name="defaultValue.enabled" />
                      </Checkbox.Wrapper>

                      {values.defaultValue?.enabled && (
                        <FormElement className="ml-[26px] !mb-4">
                          {values.element === 'date-time' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="datetime-local"
                              placeholder="Default value"
                            />
                          )}

                          {values.element === 'date' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="date"
                              placeholder="Default value"
                            />
                          )}

                          {values.element === 'time' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="time"
                              placeholder="Default value"
                            />
                          )}

                          {values.element === 'phone' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="tel"
                              placeholder="Default value"
                            />
                          )}

                          {values.element === 'country' && (
                            <Select id={`${fieldKey}_defaultValue.value`} name="defaultValue.value">
                              <option value="" default>
                                Select option
                              </option>

                              {values.limitAllowedCountries?.enabled &&
                                values.limitAllowedCountries.countries?.map(country => (
                                  <option key={country} value={country}>
                                    {countries.find(c => c.alpha2 === country).name}
                                  </option>
                                ))}

                              {!values.limitAllowedCountries?.enabled &&
                                countries.map(country => (
                                  <option key={country.alpha2} value={country.alpha2}>
                                    {country.name}
                                  </option>
                                ))}
                            </Select>
                          )}

                          {values.element === 'checkbox' && (
                            <Checkbox.Wrapper>
                              <Checkbox
                                id={`${fieldKey}_defaultValue.value`}
                                name="defaultValue.value"
                                onChange={e => {
                                  setFieldValue('defaultValue.value', e.target.checked);
                                }}
                                checked={
                                  values.defaultValue?.value !== undefined &&
                                  values.defaultValue?.value !== false &&
                                  values.defaultValue?.value !== 'false'
                                }
                              />
                              <Checkbox.Label htmlFor={`${fieldKey}_defaultValue.value`}>
                                Checked
                              </Checkbox.Label>
                            </Checkbox.Wrapper>
                          )}

                          {(values.element === 'multiple-choice' ||
                            values.element === 'select') && (
                            <Select id={`${fieldKey}_defaultValue.value`} name="defaultValue.value">
                              <option value="" default>
                                Select option
                              </option>
                              {values.options?.map(option => (
                                <option key={option.key} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          )}

                          {values.element === 'quantity' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="number"
                              placeholder="Default value"
                            />
                          )}

                          {values.element === 'number' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="number"
                              placeholder="Default value"
                            />
                          )}

                          {(values.element === 'text' ||
                            values.element === 'email' ||
                            values.element === 'textarea' ||
                            values.element === 'tax-id' ||
                            values.element === 'po-number') && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="text"
                              placeholder="Default value"
                            />
                          )}

                          <FormError name="defaultValue.value" />
                        </FormElement>
                      )}

                      <Checkbox.Wrapper>
                        <Checkbox
                          id={`${fieldKey}_showHideLogic.enabled`}
                          name="showHideLogic.enabled"
                        />
                        <Checkbox.Label htmlFor={`${fieldKey}_showHideLogic.enabled`}>
                          Conditional logic{' '}
                          <InfoTooltip>
                            {`Show this field based on another field's
                              selection.`}{' '}
                            <Link
                              to="/help/articles/fields-conditional-logic"
                              target="_blank"
                              variant="light"
                            >
                              Learn more
                            </Link>
                          </InfoTooltip>
                        </Checkbox.Label>
                      </Checkbox.Wrapper>
                      <FormError name="showHideLogic.enabled" />

                      {values.showHideLogic?.enabled && (
                        <ShowHideLogicField
                          fieldKey={fieldKey}
                          field={field}
                          fields={page.fields}
                        />
                      )}

                      <MinValueField fieldKey={fieldKey} values={values} />
                      <MaxValueField fieldKey={fieldKey} values={values} />

                      <CanUseFeature feature={Features.HIDE_FIELDS}>
                        <Checkbox.Wrapper>
                          <Checkbox id={`${fieldKey}_hidden`} name="hidden" />
                          <Checkbox.Label htmlFor={`${fieldKey}_hidden`}>
                            Hide field{' '}
                            <InfoTooltip>
                              Hide this field and pre-fill it with values from other pages & query
                              parameters.{' '}
                              <Link
                                to="/help/articles/prefill-checkout-fields"
                                target="_blank"
                                variant="light"
                              >
                                Learn more
                              </Link>
                            </InfoTooltip>
                          </Checkbox.Label>
                        </Checkbox.Wrapper>

                        <FormError name="hidden" />
                      </CanUseFeature>
                    </FormElement>

                    <FormElement>
                      <Label htmlFor={`${fieldKey}_reference`}>
                        Reference
                        <InfoTooltip>
                          Use this reference to pre-fill the form value and to use the value in
                          Zapier.{' '}
                          <Link
                            to="/help/articles/prefill-checkout-fields"
                            target="_blank"
                            variant="light"
                          >
                            Learn more
                          </Link>
                        </InfoTooltip>
                      </Label>
                      <FormElementInput className="relative">
                        <TextInput
                          id={`${fieldKey}_reference`}
                          name="reference"
                          type="text"
                          placeholder="Reference"
                        />

                        <div className="absolute top-0 right-2 bottom-0 flex items-center">
                          <CopyTextWithTooltip text={values.reference}>
                            <ClipboardIcon className="w-4 h-4" />
                          </CopyTextWithTooltip>
                        </div>
                      </FormElementInput>
                      <FormError name="reference" />
                    </FormElement>
                  </Collapsible.Content>
                </Collapsible.Root>
              </div>

              <FormErrorBase>{status && status}</FormErrorBase>

              <FormSubmit align="end">
                <FormSubmit.State visible={!!dirty} />
                <FormSubmit.Cancel
                  disabled={disabled}
                  onClick={() => {
                    if (field.isNew) {
                      onCancelAddField();
                    } else {
                      setIsEditingField(false, field._id || field.key);
                    }
                  }}
                >
                  Cancel
                </FormSubmit.Cancel>
                <FormSubmit.Button disabled={disabled}>Save</FormSubmit.Button>
              </FormSubmit>

              <div style={{ marginTop: '0.5em', color: 'var(--gray-darker)' }}>
                {field.error && field.error}
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

function hasMaxValue(element) {
  switch (element) {
    case elementFieldTypeEnums.DATE_TIME:
    case elementFieldTypeEnums.DATE:
    case elementFieldTypeEnums.TIME:
    case elementFieldTypeEnums.QUANTITY:
      return true;
    default:
      return false;
  }
}

function hasMinValue(element) {
  switch (element) {
    case elementFieldTypeEnums.DATE_TIME:
    case elementFieldTypeEnums.DATE:
    case elementFieldTypeEnums.TIME:
    case elementFieldTypeEnums.QUANTITY:
      return true;
    default:
      return false;
  }
}

function getValueTypeForElement(element) {
  switch (element) {
    case elementFieldTypeEnums.DATE_TIME:
      return 'datetime-local';
    case elementFieldTypeEnums.DATE:
      return 'date';
    case elementFieldTypeEnums.TIME:
      return 'time';
    case elementFieldTypeEnums.QUANTITY:
      return 'number';
    default:
      return null;
  }
}

function MinValueField({ fieldKey, values }) {
  if (!hasMinValue(values.element)) return null;

  const valueType = getValueTypeForElement(values.element);

  return (
    <>
      <Checkbox.Wrapper>
        <Checkbox id={`${fieldKey}_minValue.enabled`} name="minValue.enabled" />
        <Checkbox.Label htmlFor={`${fieldKey}_minValue.enabled`}>Minimum value</Checkbox.Label>
        <FormError name="minValue.enabled" />
      </Checkbox.Wrapper>

      {values.minValue?.enabled && (
        <FormElement className="ml-[26px] !mb-4">
          <TextInput
            id={`${fieldKey}_minValue.value`}
            name="minValue.value"
            type={valueType}
            placeholder="Minimum value"
          />
          <FormError name="minValue.value" />
        </FormElement>
      )}
    </>
  );
}

function MaxValueField({ fieldKey, values }) {
  if (!hasMaxValue(values.element)) return null;

  const valueType = getValueTypeForElement(values.element);

  return (
    <>
      <Checkbox.Wrapper>
        <Checkbox id={`${fieldKey}_maxValue.enabled`} name="maxValue.enabled" />
        <Checkbox.Label htmlFor={`${fieldKey}_maxValue.enabled`}>Maximum value</Checkbox.Label>
        <FormError name="maxValue.enabled" />
      </Checkbox.Wrapper>

      {values.maxValue?.enabled && (
        <FormElement className="ml-[26px] !mb-4">
          <TextInput
            id={`${fieldKey}_maxValue.value`}
            name="maxValue.value"
            type={valueType}
            placeholder="Maximum value"
          />
          <FormError name="maxValue.value" />
        </FormElement>
      )}
    </>
  );
}

function OptionsField({ options = [], onChange, fieldKey, errors }) {
  const OPTION = {
    key: uniqueId(),
    label: '',
  };

  return (
    <FormElement>
      <Label for={`${fieldKey}_options`}>Options</Label>

      <FieldArray
        name="options"
        render={arrayHelpers => (
          <>
            <Reorderable
              id={`fields.${fieldKey}.options`}
              items={options}
              onReorder={onChange}
              renderItem={({ dragHandleProps, index }) => {
                const labelError = get(errors, `options.${index}.label`);

                return (
                  <div className="flex flex-row gap-x-2 pb-2">
                    <div {...dragHandleProps} className="mt-3">
                      <DragIcon className="w-4 h-4" />
                    </div>

                    <div className="flex-1">
                      <TextInput
                        id={`options.${index}.label`}
                        name={`options.${index}.label`}
                        type="text"
                        placeholder="Label*"
                        autoFocus={!options[index].label}
                      />

                      {labelError && <FormErrorBase>{labelError}</FormErrorBase>}
                    </div>

                    <Button
                      handleClick={async () => {
                        const del = await confirm('Delete this option?', {
                          confirm: 'Delete',
                        });

                        if (del) {
                          arrayHelpers.remove(index);
                        }
                      }}
                      appearance="bare"
                      type="button"
                      className="ml-auto"
                    >
                      <XMarkIcon width="16px" height="16px" />
                    </Button>
                  </div>
                );
              }}
            />

            <Button
              handleClick={() => arrayHelpers.push(OPTION)}
              appearance="light"
              type="button"
              className="mt-2"
            >
              + Add option
            </Button>
          </>
        )}
      />

      {errors && !isArray(errors.options) && <FormError name="options" />}
    </FormElement>
  );
}

function SelectField({ options = [], onChange, fieldKey, errors }) {
  const SELECT_OPTION = {
    key: uniqueId(),
    label: '',
    value: '',
  };

  return (
    <FormElement>
      <Label for={`${fieldKey}_options`}>Options</Label>

      <FieldArray
        name="options"
        render={arrayHelpers => (
          <>
            <Reorderable
              id={`fields.${fieldKey}.options`}
              items={options}
              onReorder={onChange}
              renderItem={({ dragHandleProps, index }) => {
                const labelError = get(errors, `options.${index}.label`);
                const valueError = get(errors, `options.${index}.value`);

                return (
                  <div className="flex flex-row gap-x-2 pb-2">
                    <div {...dragHandleProps} className="mt-3">
                      <DragIcon className="w-4 h-4" />
                    </div>

                    <div className="col-span-6">
                      <TextInput
                        id={`options.${index}.label`}
                        name={`options.${index}.label`}
                        type="text"
                        placeholder="Label*"
                        autoFocus={!options[index].label}
                      />

                      {labelError && <FormErrorBase>{labelError}</FormErrorBase>}
                    </div>

                    <div className="col-span-6">
                      <TextInput
                        id={`options.${index}.value`}
                        name={`options.${index}.value`}
                        type="text"
                        placeholder="Value*"
                      />

                      {valueError && <FormErrorBase>{valueError}</FormErrorBase>}
                    </div>

                    <Button
                      handleClick={async () => {
                        const del = await confirm('Delete this option?', {
                          confirm: 'Delete',
                        });

                        if (del) {
                          arrayHelpers.remove(index);
                        }
                      }}
                      appearance="bare"
                      type="button"
                      className="ml-auto"
                    >
                      <XMarkIcon width="16px" height="16px" />
                    </Button>
                  </div>
                );
              }}
            />

            <Button
              handleClick={() => arrayHelpers.push(SELECT_OPTION)}
              appearance="light"
              type="button"
              className="mt-2"
            >
              + Add option
            </Button>
          </>
        )}
      />

      {errors && !isArray(errors.options) && <FormError name="options" />}
    </FormElement>
  );
}

FieldEdit.propTypes = propTypes;

export default FieldEdit;
